import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, BarChart, Shield, TrendingUp, Calendar, Radio, Plane, UserCheck,
  ArrowRight, ChevronRight
} from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';

// Services Data
const SERVICES = [
  {
    slug: 'sports-agency',
    icon: Users,
    title: 'Sports Agency',
    subtitle: 'Talent Management & Representation',
    description: 'Comprehensive talent management services helping athletes build successful careers through strategic representation, contract negotiation, and brand development.',
    color: 'from-green-600 to-green-700',
    features: ['Player Representation', 'Talent Scouting', 'Brand Development', 'Contract Negotiation']
  },
  {
    slug: 'sports-technology',
    icon: BarChart,
    title: 'Sports Technology',
    subtitle: 'Digital Platforms & Innovation',
    description: 'Cutting-edge digital solutions for sports organizations including membership management, tournament systems, and analytics platforms.',
    color: 'from-blue-600 to-blue-700',
    features: ['MyGolfHub Platform', 'League Management', 'Data Analytics', 'Mobile Apps'],
    featured: true
  },
  {
    slug: 'governance-advisory',
    icon: Shield,
    title: 'Governance & Advisory',
    subtitle: 'Strategic Sports Consulting',
    description: 'Expert consulting for sports bodies, government agencies, and organizations seeking best practices in sports administration.',
    color: 'from-purple-600 to-purple-700',
    features: ['Governance Frameworks', 'Compliance & Licensing', 'Strategic Planning', 'Capacity Building']
  },
  {
    slug: 'commercial-sponsorship',
    icon: TrendingUp,
    title: 'Commercial & Sponsorship',
    subtitle: 'Revenue & Brand Partnerships',
    description: 'Driving commercial success through sponsorship acquisition, brand partnerships, and innovative revenue strategies.',
    color: 'from-red-600 to-red-700',
    features: ['Sponsorship Acquisition', 'Rights Valuation', 'Brand Activation', 'Revenue Strategy']
  },
  {
    slug: 'events-operations',
    icon: Calendar,
    title: 'Events & Operations',
    subtitle: 'Tournament & Event Management',
    description: 'End-to-end event management for tournaments, championships, and corporate sports programs across Africa.',
    color: 'from-orange-600 to-orange-700',
    features: ['Tournament Management', 'Event Production', 'Logistics', 'Broadcast Support']
  },
  {
    slug: 'media-communications',
    icon: Radio,
    title: 'Media & Communications',
    subtitle: 'Sports Media & PR',
    description: 'Comprehensive media services including PR, content production, broadcasting support, and digital communications.',
    color: 'from-pink-600 to-pink-700',
    features: ['Public Relations', 'Content Production', 'Broadcasting Rights', 'Digital Marketing']
  },
  {
    slug: 'sports-tourism',
    icon: Plane,
    title: 'Sports Tourism',
    subtitle: 'Golf Tourism & Travel',
    description: 'Curating exceptional sports travel experiences including golf tourism packages and destination experiences.',
    color: 'from-cyan-600 to-cyan-700',
    features: ['Golf Tourism Packages', 'Destination Management', 'Event Travel', 'Premium Accommodation']
  },
  {
    slug: 'workforce-solutions',
    icon: UserCheck,
    title: 'Workforce Solutions',
    subtitle: 'Recruitment & Training',
    description: 'Building capacity in Africa\'s sports industry through professional recruitment and training programs.',
    color: 'from-amber-600 to-amber-700',
    features: ['Executive Search', 'Staff Recruitment', 'Training Programs', 'Volunteer Management']
  }
];

// Industries
const INDUSTRIES = [
  'Government Sports Departments',
  'National Federations',
  'Professional Clubs',
  'Schools & Universities',
  'Corporate Organizations',
  'Event Owners',
  'Sponsors & Brands',
  'Investors'
];

const ServicesPage = () => {
  return (
    <div className="min-h-screen bg-black">
      <MegaMenu />
      
      {/* Hero Section */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#008000]/10 via-black to-black" />
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-2 bg-[#008000]/20 border border-[#008000]/30 rounded-full text-[#00CC00] text-sm font-medium mb-6">
              Integrated Sports Solutions
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6">
              Expert Services for Africa's{' '}
              <span className="bg-gradient-to-r from-[#008000] to-[#00CC00] bg-clip-text text-transparent">
                Sports Ecosystem
              </span>
            </h1>
            
            <p className="text-white/70 text-lg md:text-xl max-w-3xl mx-auto mb-10 leading-relaxed">
              From talent management to technology platforms, governance consulting to event operations — 
              we deliver comprehensive solutions that transform sports organizations across the continent.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#008000] hover:bg-[#006600] text-white font-semibold rounded-lg transition-all shadow-lg shadow-[#008000]/25"
              >
                Request Proposal
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact#consultation"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all border border-white/20"
              >
                Book Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mt-4">
              What We Do
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {SERVICES.map((service, idx) => (
              <motion.div
                key={service.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link
                  to={`/services/${service.slug}`}
                  data-testid={`service-${service.slug}`}
                  className="block h-full bg-white/5 border border-white/10 rounded-xl p-6 hover:border-[#008000]/50 hover:bg-white/10 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center flex-shrink-0`}>
                      <service.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-heading font-bold text-xl group-hover:text-[#008000] transition-colors">
                          {service.title}
                        </h3>
                        {service.featured && (
                          <span className="px-2 py-0.5 bg-[#008000]/20 text-[#00CC00] text-xs font-medium rounded">
                            Featured
                          </span>
                        )}
                      </div>
                      <p className="text-[#008000] text-sm mb-3">{service.subtitle}</p>
                      <p className="text-white/60 text-sm leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {service.features.map((feature) => (
                          <span 
                            key={feature}
                            className="px-2 py-1 bg-white/5 text-white/50 text-xs rounded"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 text-[#008000] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        Learn More <ChevronRight size={16} />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">Who We Serve</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-4 mb-6">
                Industries & Sectors
              </h2>
              <p className="text-white/60 mb-8 leading-relaxed">
                Our services are designed to meet the unique needs of various stakeholders in the sports ecosystem. 
                Whether you're a government body, federation, club, or corporate sponsor, we have solutions tailored for you.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-[#008000] hover:text-[#00CC00] font-semibold transition-colors"
              >
                Discuss Your Needs <ArrowRight size={18} />
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-3"
            >
              {INDUSTRIES.map((industry, idx) => (
                <div 
                  key={industry}
                  className="bg-white/5 border border-white/10 rounded-lg p-4 hover:border-[#008000]/30 transition-colors"
                >
                  <p className="text-white font-medium text-sm">{industry}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">How We Work</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-4">
              Our Process
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Discovery', description: 'We learn about your organization, challenges, and goals through in-depth consultation.' },
              { step: '02', title: 'Strategy', description: 'Our team develops a customized solution roadmap tailored to your specific needs.' },
              { step: '03', title: 'Execution', description: 'We implement solutions with precision, keeping you informed at every stage.' },
              { step: '04', title: 'Growth', description: 'Ongoing support and optimization to ensure lasting success and continuous improvement.' }
            ].map((item, idx) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative"
              >
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <span className="text-5xl font-heading font-bold text-[#008000]/30">{item.step}</span>
                  <h3 className="text-white font-heading font-bold text-lg mt-2 mb-2">{item.title}</h3>
                  <p className="text-white/60 text-sm">{item.description}</p>
                </div>
                {idx < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ChevronRight className="w-6 h-6 text-[#008000]/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#008000] to-[#006600] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
              Ready to Transform Your Organization?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Let's discuss how our integrated services can help you achieve your goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#008000] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Request Proposal
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact#consultation"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
              >
                Book Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
